
import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';

// Unit System Context and Hooks

import { shippingLabelEndpointPrefix } from '../config';
// const shippingLabelEndpointPrefix = process.env.REACT_APP_SHIPPING_LABEL_ENDPOINT;

// Types for unit systems
export type WeightUnit = 'lb' | 'kg';
export type DimensionUnit = 'in' | 'cm';

export interface UnitConfig {
    weightUnit: WeightUnit;
    dimensionUnit: DimensionUnit;
    limits: {
        maxWeight: number;
        maxSide: number;
        secondSide: number;
        maxGirth: number;
    }
}

// Physical constants for unit conversions (these never change)
const UNIT_CONVERSION_CONSTANTS = {
    LB_TO_KG: 0.45359237,      // Exact conversion
    KG_TO_LB: 2.20462262,      // 1 / LB_TO_KG
    IN_TO_CM: 2.54,            // Exact conversion
    CM_TO_IN: 0.393700787,     // 1 / IN_TO_CM
    OZ_TO_LB: 0.0625,          // 1/16
    LB_TO_OZ: 16,
} as const;

// Shipping-specific constants
const SHIPPING_CONSTANTS = {
    VOLUMETRIC_DIVISOR_CM: 6000,  // Standard for most carriers when dimensions in cm
    VOLUMETRIC_DIVISOR_IN: 166,   // Standard for most carriers when dimensions in inches
} as const;

// Base limits in US Standard (lb, in)
const US_LIMITS = {
    maxWeight: 70,
    maxSide: 40,
    secondSide: 30,
    maxGirth: 100
};

interface UnitContextType {
    system: 'US' | 'METRIC';
    config: UnitConfig;
    setSystem: (system: 'US' | 'METRIC') => void;
    toggleSystem: () => void;
    convertWeight: (value: number, toSystem: 'US' | 'METRIC') => number;
    convertDimension: (value: number, toSystem: 'US' | 'METRIC') => number;
    convertFromBackendWeight: (value: number) => number;
    convertFromBackendDimension: (value: number) => number;
    convertToBackendWeight: (value: number) => number;
    convertToBackendDimension: (value: number) => number;
    calculateBillableWeight: (length: number, width: number, height: number, weight: number) => { value: number; unit: string };
    calculateVolumetricWeight: (length: number, width: number, height: number) => { value: number; unit: string };
    // Expose constants for use in other components
    constants: typeof UNIT_CONVERSION_CONSTANTS & typeof SHIPPING_CONSTANTS;
}

const UnitContext = createContext<UnitContextType | undefined>(undefined);

export const UnitProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    // Default to US system if not saved
    const [system, setSystemState] = useState<'US' | 'METRIC'>(() => {
        return (localStorage.getItem('unit_system') as 'US' | 'METRIC') || 'US';
    });

    const setSystem = (newSystem: 'US' | 'METRIC') => {
        setSystemState(newSystem);
        localStorage.setItem('unit_system', newSystem);
    };

    const toggleSystem = () => {
        setSystem(system === 'US' ? 'METRIC' : 'US');
    };

    const convertWeight = useCallback((value: number, toSystem: 'US' | 'METRIC'): number => {
        if (system === toSystem) return value;
        // US (lb) -> METRIC (kg)
        if (toSystem === 'METRIC') return value * UNIT_CONVERSION_CONSTANTS.LB_TO_KG;
        // METRIC (kg) -> US (lb)
        return value * UNIT_CONVERSION_CONSTANTS.KG_TO_LB;
    }, [system]);

    const convertDimension = useCallback((value: number, toSystem: 'US' | 'METRIC'): number => {
        if (system === toSystem) return value;
        // US (in) -> METRIC (cm)
        if (toSystem === 'METRIC') return value * UNIT_CONVERSION_CONSTANTS.IN_TO_CM;
        // METRIC (cm) -> US (in)
        return value * UNIT_CONVERSION_CONSTANTS.CM_TO_IN;
    }, [system]);

    // Helpers for interacting with Backend (which is always US Standard: lb, in)
    const convertFromBackendWeight = useCallback((value: number): number => {
        if (system === 'US') return value;
        return value * UNIT_CONVERSION_CONSTANTS.LB_TO_KG;
    }, [system]);

    const convertFromBackendDimension = useCallback((value: number): number => {
        if (system === 'US') return value;
        return value * UNIT_CONVERSION_CONSTANTS.IN_TO_CM;
    }, [system]);

    const convertToBackendWeight = useCallback((value: number): number => {
        if (system === 'US') return value;
        return value * UNIT_CONVERSION_CONSTANTS.KG_TO_LB;
    }, [system]);

    const convertToBackendDimension = useCallback((value: number): number => {
        if (system === 'US') return value;
        return value * UNIT_CONVERSION_CONSTANTS.CM_TO_IN;
    }, [system]);

    // Calculate current config based on system
    const currentConfig: UnitConfig = system === 'US' ? {
        weightUnit: 'lb',
        dimensionUnit: 'in',
        limits: US_LIMITS
    } : {
        weightUnit: 'kg',
        dimensionUnit: 'cm',
        limits: {
            maxWeight: parseFloat((US_LIMITS.maxWeight * UNIT_CONVERSION_CONSTANTS.LB_TO_KG).toFixed(2)),
            maxSide: parseFloat((US_LIMITS.maxSide * UNIT_CONVERSION_CONSTANTS.IN_TO_CM).toFixed(2)),
            secondSide: parseFloat((US_LIMITS.secondSide * UNIT_CONVERSION_CONSTANTS.IN_TO_CM).toFixed(2)),
            maxGirth: parseFloat((US_LIMITS.maxGirth * UNIT_CONVERSION_CONSTANTS.IN_TO_CM).toFixed(2))
        }
    };

    const calculateVolumetricWeight = useCallback((length: number, width: number, height: number): { value: number; unit: string } => {
        // Simple formula: (L * W * H) / Divisor, all in CM
        const volWeightKg = (length * UNIT_CONVERSION_CONSTANTS.IN_TO_CM * width * UNIT_CONVERSION_CONSTANTS.IN_TO_CM * height * UNIT_CONVERSION_CONSTANTS.IN_TO_CM) / SHIPPING_CONSTANTS.VOLUMETRIC_DIVISOR_CM;
        const converted = system === 'US' ? volWeightKg * UNIT_CONVERSION_CONSTANTS.KG_TO_LB : volWeightKg;

        return {
            value: converted,
            unit: system === 'US' ? 'lb' : 'kg'
        };
    }, [system]);

    const calculateBillableWeight = useCallback((length: number, width: number, height: number, weight: number): { value: number; unit: string } => {
        const vol = calculateVolumetricWeight(length, width, height);
        // Compare both in current system units
        const currentActualWeight = system === 'US' ? weight : weight * UNIT_CONVERSION_CONSTANTS.LB_TO_KG;
        return {
            value: Math.max(currentActualWeight, vol.value),
            unit: vol.unit
        };
    }, [system, calculateVolumetricWeight]);

    return (
        <UnitContext.Provider value={{
            system,
            config: currentConfig,
            setSystem,
            toggleSystem,
            convertWeight,
            convertDimension,
            convertFromBackendWeight,
            convertFromBackendDimension,
            convertToBackendWeight,
            convertToBackendDimension,
            calculateBillableWeight,
            calculateVolumetricWeight,
            constants: { ...UNIT_CONVERSION_CONSTANTS, ...SHIPPING_CONSTANTS }
        }}>
            {children}
        </UnitContext.Provider>
    );
};

export const useUnitSystem = () => {
    const context = useContext(UnitContext);
    if (!context) {
        throw new Error('useUnitSystem must be used within a UnitProvider');
    }

    // Flatten config for easier usage
    return {
        ...context,
        weightUnit: context.config.weightUnit,
        dimensionUnit: context.config.dimensionUnit,
        limits: context.config.limits
    };
};

// Default export for backward compatibility if needed, though named export is preferred
export default useUnitSystem;
